import React, { useCallback, useEffect, useMemo, useState } from 'react'
import {
  Alert,
  Button,
  Collapse,
  Empty,
  Modal,
  Progress,
  Select,
  Space,
  Table,
  Typography,
  Upload,
  message,
} from 'antd'
import type { UploadProps } from 'antd'
import { DownloadOutlined, InboxOutlined } from '@ant-design/icons'
import type { ImportOfficeTarget, ImportResultPayload, OfficeSelectOption } from '../../../lib/import-upload'
import { officeOptionKey } from '../../../lib/import-upload'

export type { ImportResultPayload } from '../../../lib/import-upload'

export type ImportColumnHint = {
  name: string
  required?: boolean
  description: string
}

export type ImportSpreadsheetPanelProps = {
  columns: ImportColumnHint[]
  accept?: string
  hint?: string
  uploadHint?: string
  allowCsv?: boolean
  onDownloadTemplate: () => void
  officeOptions?: OfficeSelectOption[]
  fixedOffice?: ImportOfficeTarget
  fixedOfficeLabel?: string
  emptyOfficeMessage?: string
  onSuccess?: () => void
  uploadRequest: (
    file: File,
    target: ImportOfficeTarget,
    onProgress: (percent: number) => void,
  ) => Promise<ImportResultPayload>
}

const { Text } = Typography
const { Dragger } = Upload

export function ImportSpreadsheetPanel({
  columns,
  accept = '.xls,.xlsx,application/vnd.ms-excel,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  hint = 'Поддерживаются файлы .xls и .xlsx. Первая строка — заголовки колонок.',
  uploadHint = 'Только .xls / .xlsx',
  allowCsv = false,
  onDownloadTemplate,
  officeOptions,
  fixedOffice,
  fixedOfficeLabel,
  emptyOfficeMessage = 'Нет доступных офисов для импорта',
  onSuccess,
  uploadRequest,
}: ImportSpreadsheetPanelProps) {
  const [uploading, setUploading] = useState(false)
  const [progress, setProgress] = useState(0)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [result, setResult] = useState<ImportResultPayload | null>(null)
  const [selectedTarget, setSelectedTarget] = useState<ImportOfficeTarget | null>(null)

  const hasOfficeSelect = Boolean(officeOptions?.length && !fixedOffice)
  const activeTarget = fixedOffice ?? selectedTarget
  const uploadDisabled = uploading || (!activeTarget && Boolean(officeOptions || fixedOffice))

  const selectOptions = useMemo(
    () =>
      (officeOptions || []).map(opt => ({
        value: officeOptionKey(opt),
        label: opt.label,
      })),
    [officeOptions],
  )

  useEffect(() => {
    if (fixedOffice) {
      setSelectedTarget(fixedOffice)
      return
    }
    if (officeOptions?.length) {
      setSelectedTarget({
        organizationId: officeOptions[0].organizationId,
        officeId: officeOptions[0].officeId,
      })
    } else {
      setSelectedTarget(null)
    }
  }, [fixedOffice, officeOptions])

  const customRequest: UploadProps['customRequest'] = options => {
    const file = options.file as File
    const { onSuccess: antSuccess, onError } = options

    if (!activeTarget) {
      message.error('Выберите офис для импорта')
      onError?.(new Error('office required'))
      return
    }

    setUploading(true)
    setProgress(0)
    setUploadError(null)
    setResult(null)

    void (async () => {
      try {
        const response = await uploadRequest(file, activeTarget, percent => setProgress(percent))
        if (response.status === 'error') {
          const msg = response.message || 'Ошибка импорта'
          setUploadError(msg)
          onError?.(new Error(msg))
          message.error(msg)
          return
        }
        setResult(response)
        antSuccess?.(response, file)
        const errCount = response.errors?.length || 0
        if (errCount > 0) {
          message.warning(`Импорт завершён с ошибками: ${errCount}`)
        } else {
          message.success('Импорт успешно выполнен')
        }
        onSuccess?.()
      } catch (err) {
        const msg = (err as Error)?.message || 'Ошибка загрузки файла'
        setUploadError(msg)
        onError?.(err as Error)
        message.error(msg)
      } finally {
        setUploading(false)
      }
    })()
  }

  const beforeUpload = (file: File) => {
    const name = file.name.toLowerCase()
    const ok =
      name.endsWith('.xls') ||
      name.endsWith('.xlsx') ||
      (allowCsv && name.endsWith('.csv'))
    if (!ok) {
      message.error(
        allowCsv
          ? 'Допустимы файлы .xls, .xlsx или .csv'
          : 'Можно загружать только файлы .xls или .xlsx',
      )
      return Upload.LIST_IGNORE
    }
    return true
  }

  const fixedLabel =
    fixedOfficeLabel ||
    (fixedOffice && officeOptions?.find(o => o.officeId === fixedOffice.officeId)?.label)

  return (
    <Space direction="vertical" size="middle" style={{ width: '100%' }}>
      {fixedOffice && (
        <Alert
          type="info"
          showIcon
          message="Офис"
          description={fixedLabel || `ID офиса: ${fixedOffice.officeId}`}
        />
      )}

      {hasOfficeSelect && (
        <div>
          <Text strong>Офис для импорта</Text>
          <Select
            style={{ width: '100%', marginTop: 8 }}
            placeholder="Выберите офис"
            value={activeTarget ? officeOptionKey(activeTarget) : undefined}
            options={selectOptions}
            onChange={value => {
              const [orgId, officeId] = String(value).split(':').map(Number)
              setSelectedTarget({ organizationId: orgId, officeId })
            }}
          />
        </div>
      )}

      {!fixedOffice && officeOptions && !officeOptions.length && (
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={emptyOfficeMessage} />
      )}

      {(fixedOffice || (officeOptions && officeOptions.length > 0)) && (
        <>
          <Space
            align="center"
            wrap
            size={8}
            style={{ width: '100%', justifyContent: 'space-between' }}
          >
            <Text type="secondary">{hint}</Text>
            <Button size="small" icon={<DownloadOutlined />} onClick={onDownloadTemplate}>
              Скачать пример .xlsx
            </Button>
          </Space>

          <Collapse
            size="small"
            ghost
            items={[
              {
                key: 'columns',
                label: 'Колонки шаблона',
                children: (
                  <Table
                    size="small"
                    pagination={false}
                    rowKey="name"
                    dataSource={columns}
                    columns={[
                      {
                        title: 'Колонка',
                        dataIndex: 'name',
                        width: 180,
                        render: (name: string, row: ImportColumnHint) => (
                          <Text>
                            {name}
                            {row.required ? <Text type="danger"> *</Text> : null}
                          </Text>
                        ),
                      },
                      { title: 'Описание', dataIndex: 'description' },
                    ]}
                  />
                ),
              },
            ]}
          />
          {!activeTarget && hasOfficeSelect && (
            <Alert type="warning" showIcon message="Выберите офис, чтобы загрузить файл." />
          )}

          <Dragger
            name="file"
            multiple={false}
            accept={accept}
            showUploadList={false}
            disabled={uploadDisabled}
            customRequest={customRequest}
            beforeUpload={beforeUpload}
            style={{ padding: 0 }}
          >
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 12,
                padding: '10px 12px',
                textAlign: 'left',
              }}
            >
              <InboxOutlined style={{ fontSize: 26, color: '#1677ff' }} />
              <div>
                <div style={{ fontSize: 14 }}>Нажмите или перетащите файл сюда</div>
                <div style={{ fontSize: 12, color: '#8c8c8c' }}>{uploadHint}</div>
              </div>
            </div>
          </Dragger>
          {uploading && (
            <div>
              <Text>Загрузка и обработка…</Text>
              <Progress percent={progress} status="active" />
            </div>
          )}

          {uploadError && (
            <Alert
              type="error"
              showIcon
              closable
              message="Ошибка импорта"
              description={uploadError}
              onClose={() => setUploadError(null)}
            />
          )}

          {result && (
            <Alert
              showIcon
              type={(result.errors?.length || 0) > 0 ? 'warning' : 'success'}
              message={`Обработано успешно: ${result.processed ?? result.successful ?? 0} из ${
                result.count ?? 0
              }`}
              description={
                (result.errors?.length || 0) > 0
                  ? `Ошибок: ${result.errors?.length}. Строки с ошибками не созданы.`
                  : 'Все строки обработаны без ошибок.'
              }
            />
          )}

          {result?.errors && result.errors.length > 0 && <CardErrors errors={result.errors} />}
        </>
      )}
    </Space>
  )
}

type ModalProps = ImportSpreadsheetPanelProps & {
  open: boolean
  title: string
  onClose: () => void
}

export function ImportSpreadsheetModal({
  open,
  title,
  onClose,
  onSuccess,
  ...panelProps
}: ModalProps) {
  const handleClose = useCallback(() => {
    onClose()
  }, [onClose])

  return (
    <Modal
      open={open}
      title={title}
      onCancel={handleClose}
      footer={[
        <Button key="close" onClick={handleClose}>
          Закрыть
        </Button>,
      ]}
      width={640}
      destroyOnClose
    >
      <ImportSpreadsheetPanel {...panelProps} onSuccess={onSuccess} />
    </Modal>
  )
}

function CardErrors({ errors }: { errors: { rowNumber: number; message: string }[] }) {
  return (
    <div
      style={{
        maxHeight: 220,
        overflowY: 'auto',
        border: '1px solid #ffccc7',
        borderRadius: 8,
        padding: '8px 12px',
        background: '#fff2f0',
      }}
    >
      <Text type="danger" strong>
        Ошибки по строкам
      </Text>
      <ul style={{ margin: '8px 0 0', paddingLeft: 18 }}>
        {errors.map((error, index) => (
          <li key={`${error.rowNumber}-${index}`}>
            <Text type="danger">
              Строка {error.rowNumber}: {error.message}
            </Text>
          </li>
        ))}
      </ul>
    </div>
  )
}
